import { Button } from '@/components/ui/button'
import { ChevronLeft } from 'lucide-react'
import { cn } from '../../../utils/cn'

interface AssessmentPaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  onPrevious?: () => void
  onNext?: () => void
  showSaveButton?: boolean
  onSave?: () => void
  className?: string
  nextLabel?: string
  disableNext?: boolean
}

export function AssessmentPagination({
  currentPage,
  totalPages,
  onPageChange,
  onPrevious,
  onSave,
  className,
  nextLabel = 'Save & Continue',
  disableNext = false,
}: AssessmentPaginationProps) {
  const canGoPrevious = currentPage > 1

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisible = 8

    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    // Always show first page
    pages.push(1)

    if (currentPage > 3) {
      pages.push('...')
    }

    // Show pages around current page
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      pages.push(i)
    }

    if (currentPage < totalPages - 2) {
      pages.push('...')
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(totalPages)
    }

    return pages
  }

  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row items-center justify-between gap-4 bg-white px-4 md:px-8 py-4',
        className
      )}
    >
      {/* Previous Button */}
      <Button
        variant='ghost'
        onClick={onPrevious}
        disabled={!canGoPrevious}
        className={cn(
          'w-full sm:w-auto min-w-[125px] h-[40px] rounded-[8px] px-4 py-2 text-[14px] font-bold order-1 sm:order-none',
          canGoPrevious
            ? 'bg-[#e0e0e0] text-[#151515] hover:bg-[#d0d0d0]'
            : 'bg-[#e0e0e0] text-[#ababab] cursor-not-allowed'
        )}
      >
        <ChevronLeft className='mr-1 h-4 w-4' />
        Previous
      </Button>

      {/* Page Numbers */}
      <div className='flex items-center gap-[21px] order-3 sm:order-none'>
        {getPageNumbers().map((page, index) => {
          if (page === '...') {
            return (
              <span key={`ellipsis-${index}`} className='px-1 text-[#232323] text-[12px]'>
                ...
              </span>
            )
          }

          const pageNumber = page as number
          const isActive = pageNumber === currentPage

          return (
            <button
              key={pageNumber}
              onClick={() => onPageChange(pageNumber)}
              className={cn(
                'flex h-[32px] w-[32px] items-center justify-center rounded-[8px] text-[12px] font-normal transition-colors',
                isActive
                  ? 'bg-[#FA6017] text-white'
                  : 'border border-[#e0e0e0] text-[#232323] hover:bg-gray-100'
              )}
            >
              {pageNumber}
            </button>
          )
        })}
      </div>

      {/* Save & Continue Button */}
      <Button
        onClick={onSave}
        disabled={disableNext}
        className={cn(
          'w-full sm:w-auto min-w-[140px] h-[40px] rounded-[8px] px-4 py-2 text-[14px] font-semibold text-white order-2 sm:order-none',
          disableNext
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-[#FA6017] hover:bg-[#e55109]'
        )}
      >
        {nextLabel}
      </Button>
    </div>
  )
}
